import { Field, Formik } from "formik";
import { Col, Row } from "react-grid-system";
import { postCreateUser, UserInput } from "../../api/admin/user";
import { StyledField } from "../../styles/StyledField";
import * as Yup from "yup";
import { SForm, SFormFooter } from "../../styles/ChangePassword";
import { Button } from "../Button";
import { Select } from "../Select";
import { UserRoles, userRoles } from "../../constants/role";
import { useState, useEffect } from "react";
import { BEErrorType } from "../ChangePassword";
import AddIcon from "../../assets/img/icons/add.svg";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { getUser } from "../../api/admin/user";

const UserInputSchema = Yup.object({
	email: Yup.string().email("Must be a valid email").required(),
	firstName: Yup.string().required(),
	lastName: Yup.string().required(),
	roleInOrganization: Yup.string(),
	phone: Yup.string().required(),
	website: Yup.string(),
	officeAddress: Yup.string(),
	privateAddress: Yup.string(),
	about: Yup.string(),
	username: Yup.string().required(),
	password: Yup.string().required(),
	role: Yup.string().required(),
});

interface CreateUserInput extends Omit<UserInput, "roles"> {
	role: string;
}

type CreateUserFormProps = {
	refetchUsers: () => void;
	closeModal: () => void;
};

export const CreateUserForm = (props: CreateUserFormProps) => {
	const { refetchUsers, closeModal } = props;

	const [beErrors, setBeErrors] = useState<BEErrorType[] | undefined>(undefined);
	const { axiosPrivate } = useAxiosPrivate();
	const [availableRoles, setAvailableRoles] = useState<{ value: UserRoles; label: string }[]>([]);

	useEffect(() => {
		const fetchUser = async () => {
			try {
				const profileRes = await axiosPrivate.get("/user/profile.json");
				const userId = profileRes.data.id;
				const user = await getUser(axiosPrivate, userId);
				const roles = user.roles;

				let currentRole: string | null = null;
				if (roles.includes("ROLE_ROOT")) {
					currentRole = "ROLE_ROOT";
				} else if (roles.includes("ROLE_ADMIN")) {
					currentRole = "ROLE_ADMIN";
				} 
				if (currentRole) {
					const filteredRoles = filterRoleOptions(currentRole as UserRoles);
					setAvailableRoles(filteredRoles);
				}				
			} catch (err) {
				console.error("Failed to fetch current user", err);
			}
		};
		fetchUser();
	}, []);

	const initialValues: CreateUserInput = {
		email: "",
		firstName: "",
		lastName: "",
		roleInOrganization: "",
		phone: "",
		website: "",
		officeAddress: "",
		privateAddress: "",
		about: "",
		username: "",
		password: "",
		role: UserRoles.ROLE_USER,
	};
	const handleSubmit = async (values: CreateUserInput) => {
		const { role, ...rest } = values;
		const data: UserInput = {
			...rest,
			roles: [role],
		};
		const res = await postCreateUser({ axiosPrivate, data });

		if (res?.data?.code === 400) {
			setBeErrors(res?.data?.fieldErrors);
			return true;
		}

		refetchUsers();
		closeModal();
		return true;
	};
	const filterRoleOptions = (role: UserRoles): { value: UserRoles; label: string }[] => {
		if (role === UserRoles.ROLE_ROOT) {
			return userRoles.filter((r) =>
				[UserRoles.ROLE_USER, UserRoles.ROLE_ADMIN].includes(r.value)
			);
		}
		if (role === UserRoles.ROLE_ADMIN) {
			return userRoles.filter((r) => r.value === UserRoles.ROLE_USER);
		}
		return [];
	};

	const handleBeError = (field: string) => {
		if (beErrors) {
			return beErrors.filter((error) => error.path === field);
		}
	};

	return (
		<Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={UserInputSchema}>
			{({ errors, touched, isValid, setFieldValue, values, isSubmitting }) => {
				return (
					<SForm>
						<Row>
							<Col>
								<StyledField>
									<label>Username</label>
									<Field name="username" type="text" />
									{errors.username && touched.username ? <span>{errors.username}</span> : null}
									{handleBeError("data.username")
										? handleBeError("data.username")?.map((err, i) => <span key={i}>{err.message}</span>)
										: null}
								</StyledField>
							</Col>
							<Col>
								<StyledField>
									<label>Role</label>
									<Select
										// @ts-expect-error to refactor
										options={availableRoles}
										value={values.role}
										onChange={(name: string, value: string) => setFieldValue(name, value) }
										id="role"
										name="role"
										data-testid="role"
									/>
									{errors.role && touched.role ? <span>{errors.role}</span> : null}
									{handleBeError("data.roles") ? handleBeError("data.roles")?.map((err, i) => <span key={i}>{err.message}</span>) : null}
								</StyledField>
							</Col>
						</Row>
						<Row>
							<Col>
								<StyledField>
									<label>First name</label>
									<Field name="firstName" type="text" />
									{errors.firstName && touched.firstName ? <span>{errors.firstName}</span> : null}
									{handleBeError("data.firstName")
										? handleBeError("data.firstName")?.map((err, i) => <span key={i}>{err.message}</span>)
										: null}
								</StyledField>
							</Col>
							<Col>
								<StyledField>
									<label>Last name</label>
									<Field name="lastName" type="text" />
									{errors.lastName && touched.lastName ? <span>{errors.lastName}</span> : null}
									{handleBeError("data.lastName")
										? handleBeError("data.lastName")?.map((err, i) => <span key={i}>{err.message}</span>)
										: null}
								</StyledField>
							</Col>
						</Row>
						<Row>
							<Col>
								<StyledField>
									<label>Email</label>
									<Field name="email" type="text" />
									{errors.email && touched.email ? <span>{errors.email}</span> : null}
									{handleBeError("data.email") ? handleBeError("data.email")?.map((err, i) => <span key={i}>{err.message}</span>) : null}
								</StyledField>
							</Col>
							<Col>
								<StyledField>
									<label>Phone</label>
									<Field name="phone" type="text" />
									{errors.phone && touched.phone ? <span>{errors.phone}</span> : null}
									{handleBeError("data.phone") ? handleBeError("data.phone")?.map((err, i) => <span key={i}>{err.message}</span>) : null}
								</StyledField>
							</Col>
						</Row>
						<Row>
							<Col>
								<StyledField>
									<label>Password</label>
									<Field name="password" type="password" />
									{errors.password && touched.password ? <span>{errors.password}</span> : null}
									{handleBeError("children[password].data")
										? handleBeError("children[password].data")?.map((err, i) => <span key={i}>{err.message}</span>)
										: null}
								</StyledField>
							</Col>
							<Col></Col>
						</Row>
						<Row>
							<Col>
								<SFormFooter>
									<Button type="reset" onClick={() => closeModal()}>
										Cancel
									</Button>
									<Button type="submit" disabled={!isValid || isSubmitting} icon={AddIcon}>
										Create
									</Button>
								</SFormFooter>
							</Col>
						</Row>
					</SForm>
				);
			}}
		</Formik>
	);
};
